import dayjs, { type Dayjs } from 'dayjs';
import type { ChannelOption } from '@/api/channel';

/**
 * Country a line item's currency implies.
 *
 * Only currencies that name one country are listed: EUR spans several of the
 * line item countries and CAD matches none, so for those the trader picks.
 */
const COUNTRY_BY_CURRENCY: Readonly<Record<string, string>> = {
  GBP: 'UK',
  USD: 'US'
};

/**
 * @param currency - ISO 4217 code, any case.
 * @returns The country the currency implies, or `undefined` when it implies none.
 */
export const countryForCurrency = (currency?: string | null): string | undefined =>
  currency ? COUNTRY_BY_CURRENCY[currency.trim().toUpperCase()] : undefined;

/**
 * The country to show after the currency changes, or `undefined` to leave it.
 *
 * A country is only replaced when it is empty or is the one the previous
 * currency implied — i.e. one this default put there. A country the trader
 * chose themselves is never overwritten.
 *
 * @param previousCurrency - Currency before the change.
 * @param nextCurrency - Currency after the change.
 * @param currentCountry - Country currently on the line.
 * @returns The country to set; `null` to clear one the old currency implied
 *   when the new currency implies none; `undefined` to leave it as it is.
 */
export const countryAfterCurrencyChange = (
  previousCurrency: string | undefined,
  nextCurrency: string | undefined,
  currentCountry: string | undefined
): string | null | undefined => {
  const wasDefaulted = !currentCountry || currentCountry === countryForCurrency(previousCurrency);
  if (!wasDefaulted) return undefined;
  const next = countryForCurrency(nextCurrency);
  if (next) return next === currentCountry ? undefined : next;
  return currentCountry ? null : undefined;
};

/**
 * The plan's campaign dates as a flight date range for a new line item.
 *
 * @param startDate - The plan's campaign start.
 * @param endDate - The plan's campaign end.
 * @returns The range, or `undefined` when either date is missing or invalid,
 *   or the range runs backwards.
 */
export const planFlightDates = (
  startDate?: string | Date | null,
  endDate?: string | Date | null
): [Dayjs, Dayjs] | undefined => {
  if (!startDate || !endDate) return undefined;
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return undefined;
  return [start, end];
};

export type ChannelLineDefaults = { targetingDetails?: string; dsp?: string };

/**
 * The targeting details and DSP a channel defaults a line item to.
 *
 * A default DSP is only used while it is still an active DSP, so one retired
 * in Trader Controls is never preselected.
 *
 * @param channel - The selected channel's label (the form stores the label).
 * @param channelOptions - Channels from the options endpoint, with their defaults.
 * @param activeDsps - Names of the DSPs the line can currently use.
 */
export const channelLineDefaults = (
  channel: string | undefined,
  channelOptions: ChannelOption[] | undefined,
  activeDsps: string[] | undefined
): ChannelLineDefaults => {
  if (!channel) return {};
  const option = channelOptions?.find((o) => o.label === channel);
  if (!option) return {};

  const targetingDetails = option.defaultTargetingDetails?.trim() || undefined;
  const dsp = option.defaultDsp && activeDsps?.includes(option.defaultDsp) ? option.defaultDsp : undefined;
  return { targetingDetails, dsp };
};
